import { useCallback, useEffect, useMemo, useRef, type PropsWithChildren } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import dataLayer, { dataLayerTrackable } from 'utils/dataLayers';
import { DEFAULT_SEO } from 'utils/constants/seo';
import { getDomainValue } from 'utils/domain';
import { useLocale } from 'hooks/useLocale';
import { getNextQueryValue } from 'utils/common';
import { useIsMounted } from 'hooks/useIsMounted';

type HeadSEOProps = {
  title?: string;
  description?: string;
  pageName?: string | null;
  gtmProps?: Record<string, any>;
};

export function HeadSEO({
  title = DEFAULT_SEO.title,
  description = DEFAULT_SEO.description,
  pageName = null,
  gtmProps = {},
  children,
}: PropsWithChildren<HeadSEOProps>) {
  const router = useRouter();
  const { profileData } = useI18n();
  const { setIsGTMReady } = useUser();
  const { country, language, locale } = useLocale();
  const isMounted = useIsMounted();
  const initGTM = useRef(false);
  const pattersonRef = useRef<MutationObserver>(null);

  const gtmPropsToUse = useMemo(() => ({ ...gtmProps, profileData, locale }), [gtmProps, profileData, locale]);

  const checkPatterson = () => {
    const [target] = document.getElementsByTagName('header');
    if (target !== undefined) {
      pattersonRef.current = new MutationObserver((mutationList) => {
        const lastMutation = mutationList[mutationList.length - 1];
        const [tnm] = lastMutation.addedNodes;
        if (tnm) {
          console.log('patterson', tnm);
          dataLayerTrackable(document.getElementsByClassName('trackable'));
        }
      });
      pattersonRef.current.observe(target, { childList: true, subtree: true });
    }
  };

  const handleDataLayer = useCallback(async () => {
    const gtm = getNextQueryValue(router.query.gtm) !== 'false';

    if (typeof window !== 'undefined' && gtm && pageName !== null) {
      if (initGTM.current === false) {
        initGTM.current = true;
        await dataLayer(pageName, language, country, gtmPropsToUse);
        if (isMounted()) {
          dataLayerTrackable(document.getElementsByClassName('trackable'));
          checkPatterson();
          setIsGTMReady(true);
        }
      }
    }
  }, [country, gtmPropsToUse, language, pageName, router.query.gtm, setIsGTMReady, isMounted]);

  useEffect(() => {
    const onRouteChange = () => {
      initGTM.current = false;
      handleDataLayer();
    };
    // si la pagina cambia por el router, se vuelve a inicializar el GTM
    router.events.on('routeChangeComplete', onRouteChange);

    handleDataLayer();

    return () => {
      router.events.off('routeChangeComplete', onRouteChange);
      // limpamos el observer de patterson si existe
      if (pattersonRef.current) {
        pattersonRef.current.disconnect();
      }
    };
  }, [handleDataLayer, router.events]);

  return (
    <Head>
      <title>{title}</title>
      {title && <meta name="title" content={title} />}
      {description && <meta name="description" content={description} />}
      {children}
    </Head>
  );
}
